import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Divider,
  Avatar,
  CircularProgress,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  Store as StoreIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import permissionService from '../../services/permissionService';
import { localTodayIso } from '../../utils/localDate';

const OrdenesTrabajoList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [executiveFilter, setExecutiveFilter] = useState('ALL');
  const [executiveOptions, setExecutiveOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [entityAssignments, setEntityAssignments] = useState([]);
  const [loadingEntityAssignments, setLoadingEntityAssignments] = useState(false);
  const [workOrderEntityMap, setWorkOrderEntityMap] = useState({});
  // Servicios contratados por ente (quotation_entity_services) de cada OT, para desglosar la
  // columna "Servicio" cuando los entes no comparten el mismo servicio.
  const [entityServicesByOrder, setEntityServicesByOrder] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    overdue: 0
  });
  const [canDeleteOrders, setCanDeleteOrders] = useState(false);
  const [assignedUserIdsByOrder, setAssignedUserIdsByOrder] = useState({});
  // Detalle crudo por ente (entityName, assignedInspector, scheduledDate) de cada OT,
  // para desglosar la columna "Asignado a" cuando hay varios ejecutivos distintos.
  const [assignmentDetailsByOrder, setAssignmentDetailsByOrder] = useState({});
  // Filas de la columna "Entes" que el usuario expandió manualmente para ver todos los entes.
  const [expandedEntityRows, setExpandedEntityRows] = useState({});

  const currentUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
  // userInfo.role viene de permissionService (GET /users/current-permissions) como objeto
  // {id, name, description}, NO como string plano -- de ahí que comparar currentUser.role
  // directo contra 'EJECUTIVO_OPERACIONES' nunca coincidía y la restricción no aplicaba.
  const currentUserRoleName = currentUser?.role?.name || currentUser?.role;
  // Un Ejecutivo de Operaciones solo debe poder abrir las órdenes donde aparece
  // asignado en al menos un ente (work_order_details.assignedUserId); Admin y
  // Coordinador de Operaciones necesitan ver/abrir todas para poder asignarlas.
  const isRestrictedExecutive = currentUserRoleName === 'EJECUTIVO_OPERACIONES';
  const canOpenOrder = (order) =>
    !isRestrictedExecutive || (assignedUserIdsByOrder[order.id] || []).includes(currentUser.id);
  // Un ente "es mío" si el detalle (work_order_details) de ese ente en esa OT tiene
  // al usuario que está viendo la pantalla como assignedUserId.
  const isEntityMine = (orderId, entityName) =>
    (assignmentDetailsByOrder[orderId] || []).some(
      d => d.entityName === entityName && d.assignedUserId === currentUser.id
    );
  const toggleEntityRow = (orderId) =>
    setExpandedEntityRows(prev => ({ ...prev, [orderId]: !prev[orderId] }));

  // Lleva a la pantalla de la empresa (Matriz) o de la sucursal correspondiente al ente.
  const handleEntityChipClick = (order, entity) => {
    if (entity.type === 'MATRIZ') {
      if (!order.companyId) return;
      navigate('/clientes/empresas', { state: { highlightEntityId: order.companyId, entityType: 'MATRIZ' } });
    } else {
      if (!entity.branchId) return;
      navigate('/clientes/sucursales', { state: { highlightEntityId: entity.branchId, entityType: 'SUCURSAL' } });
    }
  };

  useEffect(() => {
    loadWorkOrders();
    loadExecutiveOptions();
    permissionService.canDelete('/servicios/ordenes-trabajo').then(setCanDeleteOrders);
  }, []);

  // Opciones del filtro "Ejecutivo asignado" -- usuarios reales con rol EJECUTIVO_OPERACIONES,
  // no solo los que ya aparecen con alguna OT asignada. /users/executives es de lectura mínima
  // y abierto a cualquier usuario autenticado (a diferencia de /users, que es solo ADMIN),
  // porque esta pantalla la usan también Coordinador de Operaciones y Supervisor de Calidad.
  // Mismo formato de nombre "firstName firstLastName" que WorkOrderDetailManager.jsx guarda en
  // work_order_details.assignedInspector.
  const loadExecutiveOptions = async () => {
    try {
      const response = await api.get('/users/executives');
      const names = (Array.isArray(response.data) ? response.data : [])
        .map(u => `${u.firstName} ${u.firstLastName}`.trim())
        .filter(Boolean);
      setExecutiveOptions([...new Set(names)].sort());
    } catch (err) {
      console.error('Error cargando ejecutivos de operaciones:', err);
    }
  };

  useEffect(() => {
    filterOrders();
  }, [searchTerm, statusFilter, executiveFilter, dateFrom, dateTo, workOrders, assignmentDetailsByOrder]);

  // Cualquier cambio de filtro regresa a la primera página -- si no, se puede quedar viendo una
  // página vacía si el filtro nuevo tiene menos resultados que la página en la que estaba.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, executiveFilter, dateFrom, dateTo]);

  // Si llegamos desde otra pantalla (ej. Tablero de Seguimiento) con una OT específica
  // a abrir, abrimos directamente su diálogo de detalle en cuanto la lista esté cargada.
  useEffect(() => {
    if (!location.state?.highlightWorkOrderId || workOrders.length === 0) return;
    const order = workOrders.find(o => String(o.id) === String(location.state.highlightWorkOrderId));
    if (order) {
      handleViewDetails(order);
    }
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [workOrders, location.state]);

  const loadWorkOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      // Obtener todas las órdenes sin paginación (size=1000)
      const response = await api.get('/work-orders', {
        params: {
          page: 0,
          size: 1000,
          sortBy: 'createdAt',
          sortDir: 'desc'
        }
      });
      
      // El backend devuelve datos paginados
      const orders = response.data.content || response.data || [];
      
      // Mapear los datos del backend al formato del frontend
      const mappedOrders = orders.map(order => ({
        id: order.id,
        companyId: order.companyId,
        folio: order.orderNumber, // orderNumber es el folio en el backend
        quotationFolio: order.quotationFolio || 'N/A',
        companyName: order.companyName || 'Sin empresa',
        serviceName: order.serviceType || 'Sin servicio',
        assignedTo: order.assignedInspector || 'Sin asignar',
        status: order.status || 'PENDIENTE',
        createdAt: order.createdAt,
        dueDate: order.scheduledDate,
        scheduledTimeStart: order.scheduledTimeStart || null,
        scheduledTimeEnd: order.scheduledTimeEnd || null,
        completedAt: order.completedAt,
        totalAmount: order.totalAmount || 0
      }));

      setWorkOrders(mappedOrders);
      calculateStats(mappedOrders);
      loadAssignedSummaries(mappedOrders);
      loadEntityAssignments(mappedOrders);
    } catch (err) {
      console.error('Error cargando órdenes de trabajo:', err);
      setError('Error al cargar las órdenes de trabajo. Mostrando datos de ejemplo.');
      // Datos de prueba en caso de error
      const mockOrders = generateMockOrders();
      setWorkOrders(mockOrders);
      calculateStats(mockOrders);
    } finally {
      setLoading(false);
    }
  };

  // "Asignado a" real no vive en WorkOrder.assignedInspector (nunca se llena desde ningún
  // lugar de la app) sino por ente en work_order_details.assignedInspector (lo llena Agenda
  // de Servicios). Traemos los entes de todas las órdenes de una sola vez y resumimos.
  const summarizeAssignment = (details) => {
    if (!details || details.length === 0) return 'Sin asignar';
    const assigned = details.filter(d => d.assignedInspector);
    if (assigned.length === 0) return 'Sin asignar';
    const uniqueNames = [...new Set(assigned.map(d => d.assignedInspector))];
    if (assigned.length < details.length) return `${assigned.length}/${details.length} asignados`;
    if (uniqueNames.length === 1) return uniqueNames[0];
    return 'Varios inspectores';
  };

  const loadAssignedSummaries = async (orders) => {
    if (!orders.length) return;
    try {
      const response = await api.get('/work-order-details/by-work-order-ids', {
        params: { ids: orders.map(o => o.id).join(',') }
      });
      const details = Array.isArray(response.data) ? response.data : [];
      const byOrderId = {};
      details.forEach(d => {
        if (!byOrderId[d.workOrderId]) byOrderId[d.workOrderId] = [];
        byOrderId[d.workOrderId].push(d);
      });
      setWorkOrders(prev => prev.map(order => ({
        ...order,
        assignedTo: summarizeAssignment(byOrderId[order.id])
      })));
      setAssignmentDetailsByOrder(byOrderId);

      const userIdsByOrder = {};
      Object.entries(byOrderId).forEach(([orderId, orderDetails]) => {
        userIdsByOrder[orderId] = [...new Set(
          orderDetails.filter(d => d.assignedUserId != null).map(d => d.assignedUserId)
        )];
      });
      setAssignedUserIdsByOrder(userIdsByOrder);
    } catch (err) {
      console.error('Error cargando resumen de asignación por ente:', err);
    }
  };

  // Entes (matriz y sucursales) que contiene cada OT. Usamos /sync (no el GET plano) porque
  // crea el detalle faltante para entes que aún no tienen fila en work_order_details (p. ej.
  // sucursales agregadas después), igual que hace Programas de Servicios.
  const loadEntityAssignments = async (orders) => {
    if (!orders.length) {
      setWorkOrderEntityMap({});
      setEntityServicesByOrder({});
      return;
    }
    try {
      const entries = await Promise.all(
        orders.map(async (order) => {
          try {
            const response = await api.post(`/work-order-details/work-order/${order.id}/sync`);
            const details = Array.isArray(response.data) ? response.data : [];
            const activeDetails = details.filter(d => d?.status !== 'CANCELADA');
            const entityMap = new Map();
            activeDetails.forEach(d => {
              if (d?.entityName && !entityMap.has(d.entityName)) {
                entityMap.set(d.entityName, { type: d.entityType, branchId: d.branchId });
              }
            });
            const entities = Array.from(entityMap.entries()).map(([name, info]) => ({ name, ...info }));
            // Servicios por ente -- el WorkOrder solo guarda un serviceType heredado del campo
            // legacy quotations.service_id (siempre el de la matriz), así que si cada ente compró
            // un servicio distinto (ej. Riesgo Bajo en matriz, Riesgo Medio en una sucursal) esa
            // diferencia solo se ve aquí, no en order.serviceName.
            const servicesForOrder = activeDetails.map(d => ({
              entityName: d.entityName || (d.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'),
              serviceNames: Array.isArray(d.serviceNames) ? d.serviceNames : [],
              riskLevelId: d.riskLevelId ?? null
            }));
            return [order.id, { entities, servicesForOrder }];
          } catch {
            return [order.id, { entities: [], servicesForOrder: [] }];
          }
        })
      );
      setWorkOrderEntityMap(Object.fromEntries(entries.map(([id, v]) => [id, v.entities])));
      setEntityServicesByOrder(Object.fromEntries(entries.map(([id, v]) => [id, v.servicesForOrder])));
    } catch (err) {
      console.error('Error cargando entes por orden de trabajo:', err);
    }
  };

  const generateMockOrders = () => {
    return [
      {
        id: 1,
        folio: 'OT-2026-001',
        quotationFolio: 'COT-2026-001',
        companyName: 'Empresa Demo S.A. de C.V.',
        serviceName: 'Programa de Protección Civil',
        assignedTo: 'Juan Pérez',
        status: 'EN_PROGRESO',
        createdAt: '2026-01-20T10:00:00',
        dueDate: '2026-02-20T10:00:00',
        totalAmount: 15000.00
      },
      {
        id: 2,
        folio: 'OT-2026-002',
        quotationFolio: 'COT-2026-002',
        companyName: 'Constructora XYZ',
        serviceName: 'Estudio de Riesgo',
        assignedTo: 'María González',
        status: 'PENDIENTE',
        createdAt: '2026-01-22T14:30:00',
        dueDate: '2026-02-15T14:30:00',
        totalAmount: 25000.00
      },
      {
        id: 3,
        folio: 'OT-2026-003',
        quotationFolio: 'COT-2026-003',
        companyName: 'Industrias ABC',
        serviceName: 'Inspección de Seguridad',
        assignedTo: 'Carlos Ramírez',
        status: 'COMPLETADA',
        createdAt: '2026-01-15T09:00:00',
        dueDate: '2026-01-30T09:00:00',
        completedAt: '2026-01-28T16:00:00',
        totalAmount: 12000.00
      },
      {
        id: 4,
        folio: 'OT-2026-004',
        quotationFolio: 'COT-2026-004',
        companyName: 'Comercializadora DEF',
        serviceName: 'Programa de Protección Civil',
        assignedTo: 'Ana López',
        status: 'PENDIENTE',
        createdAt: '2026-01-24T11:00:00',
        dueDate: '2026-02-25T11:00:00',
        totalAmount: 18000.00
      }
    ];
  };

  // Estados reales del backend (WorkOrder.WorkOrderStatus): PENDIENTE, ASIGNADA, PROGRAMADA,
  // EN_PROGRESO, LISTA_PARA_REVISION, COMPLETADA, CANCELADA — se agrupan en 4 categorías para
  // las tarjetas de estadísticas, igual que antes.
  const IN_PROGRESS_STATUSES = ['ASIGNADA', 'PROGRAMADA', 'EN_PROGRESO', 'LISTA_PARA_REVISION'];
  const TERMINAL_STATUSES = ['COMPLETADA', 'CANCELADA'];

  // Vencida: tiene fecha de vencimiento ya pasada y no llegó a un estado terminal.
  // Comparación por string de fecha local (YYYY-MM-DD), no por Date -- `new Date(dueDate) <
  // new Date()` sufre el mismo corrimiento de zona horaria que formatDate (ver arriba) y
  // marcaba órdenes como vencidas medio día antes de tiempo en huso horario de México.
  const isOverdue = (order) => {
    if (!order.dueDate || TERMINAL_STATUSES.includes(order.status)) return false;
    const todayLocal = new Date().toLocaleDateString('en-CA');
    return order.dueDate.substring(0, 10) < todayLocal;
  };

  const calculateStats = (orders) => {
    const stats = {
      total: orders.length,
      pending: orders.filter(o => o.status === 'PENDIENTE').length,
      inProgress: orders.filter(o => IN_PROGRESS_STATUSES.includes(o.status)).length,
      completed: orders.filter(o => o.status === 'COMPLETADA').length,
      cancelled: orders.filter(o => o.status === 'CANCELADA').length,
      overdue: orders.filter(isOverdue).length
    };
    setStats(stats);
  };

  const filterOrders = () => {
    let filtered = [...workOrders];

    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        order.folio?.toLowerCase().includes(term) ||
        order.quotationFolio?.toLowerCase().includes(term) ||
        order.companyName?.toLowerCase().includes(term) ||
        order.serviceName?.toLowerCase().includes(term) ||
        order.assignedTo?.toLowerCase().includes(term)
      );
    }

    // Filtrar por estado
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filtrar por ejecutivo asignado (considera cualquier ente de la OT, no solo el resumen)
    if (executiveFilter !== 'ALL') {
      filtered = filtered.filter(order => {
        const details = assignmentDetailsByOrder[order.id];
        if (details && details.length > 0) {
          return details.some(d => d.assignedInspector === executiveFilter);
        }
        return order.assignedTo === executiveFilter;
      });
    }

    // Filtrar por rango de fecha de creación
    if (dateFrom) {
      filtered = filtered.filter(order => order.createdAt && order.createdAt.substring(0, 10) >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(order => order.createdAt && order.createdAt.substring(0, 10) <= dateTo);
    }

    setFilteredOrders(filtered);
  };


  // Ordenamiento de la tabla (elegido por el usuario: Folio, Cotización o Nombre Comercial)
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedOrders = !sortField ? filteredOrders : [...filteredOrders].sort((a, b) => {
    const valueA = (a[sortField] || '').toString().toLowerCase();
    const valueB = (b[sortField] || '').toString().toLowerCase();
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Nivel de riesgo por ente (WorkOrderDetail.riskLevelId, ya resuelto por el backend a partir
  // de los servicios contratados -- ver WorkOrderDetailService.syncRiskLevelFromServices).
  const RISK_LEVEL_LABELS = { 1: 'Bajo', 2: 'Medio', 3: 'Alto' };
  const RISK_LEVEL_COLORS = { 1: '#4caf50', 2: '#ff9800', 3: '#f44336' };

  const paginatedOrders = sortedOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Exporta las órdenes ya filtradas (no solo la página visible) a un .csv que Excel abre
  // directo -- mismo desglose por ente que ya se ve en pantalla para Entes/Servicio/Asignado a.
  const handleExportCsv = () => {
    const escapeCsv = (value) => {
      const str = String(value ?? '');
      return /[",\n;]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const headers = ['Folio', 'Cotización', 'Nombre Comercial', 'Entes', 'Servicio', 'Asignado a', 'Estado', 'Fecha Creación', 'Vencimiento'];

    const rows = sortedOrders.map(order => {
      const entes = (workOrderEntityMap[order.id] || []).map(e => e.name).join(' | ');

      const servicesForOrder = entityServicesByOrder[order.id] || [];
      const distinctServiceNames = [...new Set(servicesForOrder.flatMap(e => e.serviceNames))];
      const servicio = distinctServiceNames.length <= 1
        ? order.serviceName
        : servicesForOrder.map(e => `${e.entityName}: ${e.serviceNames.join(', ') || 'Sin servicio'}`).join(' | ');

      const details = assignmentDetailsByOrder[order.id] || [];
      const uniqueInspectors = [...new Set(details.filter(d => d.assignedInspector).map(d => d.assignedInspector))];
      const asignado = uniqueInspectors.length <= 1
        ? order.assignedTo
        : details.map(d => `${d.entityName || 'Ente'}: ${d.assignedInspector || 'Sin asignar'}`).join(' | ');

      return [
        order.folio, order.quotationFolio, order.companyName, entes, servicio, asignado,
        getStatusLabel(order.status), formatDate(order.createdAt),
        order.dueDate ? formatDate(order.dueDate) : 'Sin fecha'
      ];
    });

    // BOM al inicio para que Excel detecte UTF-8 y no rompa acentos/ñ
    const csvContent = [headers, ...rows].map(row => row.map(escapeCsv).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ordenes_de_trabajo_${localTodayIso()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDIENTE: 'warning',
      ASIGNADA: 'info',
      PROGRAMADA: 'primary',
      EN_PROGRESO: 'secondary',
      LISTA_PARA_REVISION: 'default',
      COMPLETADA: 'success',
      CANCELADA: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      PENDIENTE: 'Pendiente de asignación de Ejecutivo de Operaciones',
      ASIGNADA: 'Asignada',
      PROGRAMADA: 'Programada',
      EN_PROGRESO: 'En Progreso',
      LISTA_PARA_REVISION: 'Lista para Revisión',
      COMPLETADA: 'Completada',
      CANCELADA: 'Cancelada'
    };
    return labels[status] || status;
  };

  // Si viene "YYYY-MM-DD" (LocalDate, sin hora) parseamos los componentes a mano -- `new
  // Date("YYYY-MM-DD")` lo interpreta como medianoche UTC y en huso horario de México (UTC-6)
  // se desplaza un día hacia atrás al convertir a local (ej. 2026-09-11 se mostraba 10/09/2026).
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
    const date = dateOnlyMatch
      ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
      : new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleViewDetails = async (order) => {
    if (!canOpenOrder(order)) return;
    setSelectedOrder(order);
    setDetailsOpen(true);
    setEntityAssignments([]);
    setLoadingEntityAssignments(true);
    try {
      // /sync (no el GET plano) crea el detalle faltante para entes sin fila aún en
      // work_order_details, para que se vean todos los entes de la orden (matriz y sucursales).
      const response = await api.post(`/work-order-details/work-order/${order.id}/sync`);
      setEntityAssignments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error cargando asignación por ente:', err);
      setEntityAssignments([]);
    } finally {
      setLoadingEntityAssignments(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
    setEntityAssignments([]);
  };

  const handleDelete = async (orderId) => {
    if (window.confirm('¿Está seguro de eliminar esta orden de trabajo?')) {
      try {
        await api.delete(`/work-orders/${orderId}`);
        loadWorkOrders();
      } catch (err) {
        console.error('Error eliminando orden:', err);
        setError('Error al eliminar la orden de trabajo');
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Órdenes de Trabajo
          </Typography>
        </Box>
        <IconButton
          onClick={loadWorkOrders}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'rotate(180deg)',
              transition: 'all 0.3s ease-in-out'
            }
          }}
          title="Actualizar lista de órdenes de trabajo"
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex' }}>
          <Card sx={{
            height: '120px', width: '100%', display: 'flex', alignItems: 'center',
            '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
          }}>
            <CardContent sx={{ textAlign: 'center', width: '100%' }}>
              <AssignmentIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">Total</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex' }}>
          <Card sx={{
            bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800',
            height: '120px', width: '100%', display: 'flex', alignItems: 'center',
            '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
          }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#ff9800">Pendientes</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex' }}>
          <Card sx={{
            bgcolor: '#e3f2fd', borderLeft: '4px solid #1976d2',
            height: '120px', width: '100%', display: 'flex', alignItems: 'center',
            '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
          }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#1976d2">En Progreso</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.inProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex' }}>
          <Card sx={{
            bgcolor: '#e8f5e8', borderLeft: '4px solid #4caf50',
            height: '120px', width: '100%', display: 'flex', alignItems: 'center',
            '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
          }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#4caf50">Completadas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex' }}>
          <Card sx={{
            bgcolor: '#ffebee', borderLeft: '4px solid #f44336',
            height: '120px', width: '100%', display: 'flex', alignItems: 'center',
            '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
          }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#f44336">Canceladas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.cancelled}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex' }}>
          <Card sx={{
            bgcolor: '#fce4ec', borderLeft: '4px solid #c2185b',
            height: '120px', width: '100%', display: 'flex', alignItems: 'center',
            '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
          }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#c2185b">Vencidas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.overdue}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Buscar por folio, empresa, servicio o asignado..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  label="Estado"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="ALL">Todos</MenuItem>
                  <MenuItem value="PENDIENTE">Pendiente de asignación de Ejecutivo de Operaciones</MenuItem>
                  <MenuItem value="ASIGNADA">Asignada</MenuItem>
                  <MenuItem value="PROGRAMADA">Programada</MenuItem>
                  <MenuItem value="EN_PROGRESO">En Progreso</MenuItem>
                  <MenuItem value="LISTA_PARA_REVISION">Lista para Revisión</MenuItem>
                  <MenuItem value="COMPLETADA">Completada</MenuItem>
                  <MenuItem value="CANCELADA">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Ejecutivo asignado</InputLabel>
                <Select
                  value={executiveFilter}
                  label="Ejecutivo asignado"
                  onChange={(e) => setExecutiveFilter(e.target.value)}
                >
                  <MenuItem value="ALL">Todos</MenuItem>
                  {executiveOptions.map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Creación desde"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Creación hasta"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Mostrando {filteredOrders.length} de {workOrders.length} órdenes
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportCsv}
                disabled={filteredOrders.length === 0}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Exportar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortField === 'folio'}
                    direction={sortField === 'folio' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('folio')}
                  >
                    Folio
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortField === 'quotationFolio'}
                    direction={sortField === 'quotationFolio' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('quotationFolio')}
                  >
                    Cotización
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortField === 'companyName'}
                    direction={sortField === 'companyName' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('companyName')}
                  >
                    Nombre Comercial
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Entes</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Servicio</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Riesgo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Asignado a</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fecha Creación</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Vencimiento</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography>Cargando órdenes de trabajo...</Typography>
                  </TableCell>
                </TableRow>
              ) : sortedOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No se encontraron órdenes de trabajo
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {order.folio}
                      </Typography>
                    </TableCell>
                    <TableCell>{order.quotationFolio}</TableCell>
                    <TableCell>{order.companyName}</TableCell>
                    <TableCell>
                      {(workOrderEntityMap[order.id] || []).length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {(expandedEntityRows[order.id]
                            ? (workOrderEntityMap[order.id] || [])
                            : (workOrderEntityMap[order.id] || []).slice(0, 2)
                          ).map((entity) => {
                            const isMatriz = entity.type === 'MATRIZ';
                            const displayName = isMatriz ? entity.name.replace(/\s*\(Matriz\)\s*$/i, '') : entity.name;
                            const isMine = isEntityMine(order.id, entity.name);
                            return (
                              <Tooltip
                                key={`${order.id}-${entity.name}`}
                                title={`Ver ${isMatriz ? 'empresa' : 'sucursal'}${isMine ? ' • Asignado a ti' : ''}`}
                              >
                                <Chip
                                  icon={isMatriz
                                    ? <AccountBalanceIcon sx={{ fontSize: '14px !important' }} />
                                    : <StoreIcon sx={{ fontSize: '14px !important' }} />}
                                  label={displayName}
                                  size="small"
                                  onClick={() => handleEntityChipClick(order, entity)}
                                  sx={{
                                    bgcolor: isMatriz ? '#ede7f6' : '#e1f5fe',
                                    color: isMatriz ? '#4527a0' : '#01579b',
                                    fontWeight: 600, fontSize: '0.7rem', height: 22, maxWidth: 190,
                                    justifyContent: 'flex-start',
                                    cursor: 'pointer',
                                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                    '&:hover': { bgcolor: isMatriz ? '#d9cdf0' : '#c5e9fb' },
                                    ...(isMine && {
                                      border: '2px solid #1976d2',
                                      boxShadow: '0 0 0 2px #e3f2fd',
                                    }),
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                          {(workOrderEntityMap[order.id] || []).length > 2 && (
                            <Button
                              size="small"
                              onClick={() => toggleEntityRow(order.id)}
                              startIcon={expandedEntityRows[order.id]
                                ? <ExpandLessIcon sx={{ fontSize: 16 }} />
                                : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                fontSize: '0.7rem', minWidth: 0, p: 0, alignSelf: 'flex-start',
                                textTransform: 'none', color: '#1976d2', fontWeight: 600
                              }}
                            >
                              {expandedEntityRows[order.id]
                                ? 'Ver menos'
                                : `+${(workOrderEntityMap[order.id] || []).length - 2} más`}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Sin ente asignado</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const servicesForOrder = entityServicesByOrder[order.id] || [];
                        const distinctServiceNames = [...new Set(servicesForOrder.flatMap(e => e.serviceNames))];
                        if (distinctServiceNames.length <= 1) {
                          return order.serviceName;
                        }
                        // Entes con servicios distintos (ej. Riesgo Bajo en matriz, Riesgo Medio
                        // en una sucursal): desglosar en vez de mostrar solo order.serviceName.
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {servicesForOrder.map((e, i) => (
                              <Box key={`${order.id}-${e.entityName}-${i}`} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {e.entityName.replace(/\s*\(Matriz\)\s*$/i, '')}:
                                </Typography>
                                <Typography variant="caption" sx={{ color: e.serviceNames.length ? '#1976d2' : '#94a3b8' }}>
                                  {e.serviceNames.length ? e.serviceNames.join(', ') : 'Sin servicio'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const servicesForOrder = entityServicesByOrder[order.id] || [];
                        const distinctRisk = [...new Set(
                          servicesForOrder.map(e => e.riskLevelId).filter(r => r != null)
                        )];
                        const riskChip = (id) => (
                          <Chip
                            label={RISK_LEVEL_LABELS[id] || 'N/D'}
                            size="small"
                            sx={{
                              bgcolor: `${RISK_LEVEL_COLORS[id] || '#9e9e9e'}22`,
                              color: RISK_LEVEL_COLORS[id] || '#9e9e9e',
                              fontWeight: 700, fontSize: '0.7rem', height: 22
                            }}
                          />
                        );
                        if (distinctRisk.length === 0) {
                          return <Typography variant="caption" color="text.secondary">Sin definir</Typography>;
                        }
                        if (distinctRisk.length === 1) {
                          return riskChip(distinctRisk[0]);
                        }
                        // Entes con nivel de riesgo distinto: desglosar igual que Servicio/Asignado a.
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {servicesForOrder.map((e, i) => (
                              <Box key={`${order.id}-risk-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {e.entityName.replace(/\s*\(Matriz\)\s*$/i, '')}:
                                </Typography>
                                {e.riskLevelId != null ? riskChip(e.riskLevelId) : (
                                  <Typography variant="caption" color="text.secondary">Sin definir</Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const details = assignmentDetailsByOrder[order.id] || [];
                        const uniqueInspectors = [...new Set(
                          details.filter(d => d.assignedInspector).map(d => d.assignedInspector)
                        )];
                        if (uniqueInspectors.length <= 1) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: '#1976d2', fontSize: '0.875rem' }}>
                                {order.assignedTo?.charAt(0)}
                              </Avatar>
                              {order.assignedTo}
                            </Box>
                          );
                        }
                        // Varios ejecutivos distintos entre los entes de esta OT: desglosar
                        // ente -> ejecutivo -> fecha programada en vez del texto genérico.
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {details.map(d => (
                              <Box key={d.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {(d.entityName || (d.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal')).replace(/\s*\(Matriz\)\s*$/i, '')}:
                                </Typography>
                                <Typography variant="caption" sx={{
                                  color: d.assignedInspector ? '#1976d2' : '#94a3b8',
                                  fontWeight: d.assignedInspector ? 600 : 400
                                }}>
                                  {d.assignedInspector || 'Sin asignar'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {d.scheduledDate ? `(${formatDate(d.scheduledDate)})` : '(sin programar)'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {order.status === 'PENDIENTE' ? (
                        <Tooltip title="Para asignar un ejecutivo, ve a Servicios > Agenda de Servicios">
                          <Chip
                            label={getStatusLabel(order.status)}
                            color={getStatusColor(order.status)}
                            size="small"
                            sx={{ fontWeight: 600, cursor: 'help' }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label={getStatusLabel(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>
                      {order.dueDate ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {isOverdue(order) && (
                            <Tooltip title="Orden vencida">
                              <WarningIcon sx={{ fontSize: 16, color: '#c2185b' }} />
                            </Tooltip>
                          )}
                          <Typography
                            variant="body2"
                            sx={isOverdue(order)
                              ? { color: '#c2185b', fontWeight: 700 }
                              : { color: 'text.primary' }}
                          >
                            {formatDate(order.dueDate)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Sin fecha</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={canOpenOrder(order) ? 'Ver detalles' : 'Solo puedes abrir órdenes que tengas asignadas'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(order)}
                              disabled={!canOpenOrder(order)}
                              sx={{ color: '#1976d2' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {canDeleteOrders && (
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(order.id)}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedOrders.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 0.8,
              display: 'flex', alignItems: 'center'
            }}>
              <AssignmentIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>
                Orden de Trabajo
              </Typography>
              {selectedOrder && (
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem' }}>
                  {selectedOrder.folio}
                </Typography>
              )}
            </Box>
          </Box>
          {selectedOrder && (
            selectedOrder.status === 'PENDIENTE' ? (
              <Tooltip title="Para asignar un ejecutivo, ve a Servicios > Agenda de Servicios">
                <Chip
                  label={getStatusLabel(selectedOrder.status)}
                  color={getStatusColor(selectedOrder.status)}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'help' }}
                />
              </Tooltip>
            ) : (
              <Chip
                label={getStatusLabel(selectedOrder.status)}
                color={getStatusColor(selectedOrder.status)}
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.75rem' }}
              />
            )
          )}
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {selectedOrder && (
            <Box>
              {/* Sección: Información General */}
              <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Typography sx={{
                  fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5
                }}>
                  Información General
                </Typography>
                {[
                  { icon: <DescriptionIcon sx={{ fontSize: 15, color: '#1976d2' }} />, label: 'Folio OT', value: selectedOrder.folio },
                  { icon: <DescriptionIcon sx={{ fontSize: 15, color: '#64748b' }} />, label: 'Cotización', value: selectedOrder.quotationFolio },
                  { icon: <BusinessIcon sx={{ fontSize: 15, color: '#64748b' }} />, label: 'Nombre Comercial', value: selectedOrder.companyName },
                  { icon: <TimelineIcon sx={{ fontSize: 15, color: '#64748b' }} />, label: 'Estado', chip: (
                    selectedOrder.status === 'PENDIENTE' ? (
                      <Tooltip title="Para asignar un ejecutivo, ve a Servicios > Agenda de Servicios">
                        <Chip label={getStatusLabel(selectedOrder.status)} color={getStatusColor(selectedOrder.status)} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22, cursor: 'help' }} />
                      </Tooltip>
                    ) : (
                      <Chip label={getStatusLabel(selectedOrder.status)} color={getStatusColor(selectedOrder.status)} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }} />
                    )
                  )},
                ].map((row, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center',
                    py: 1, borderBottom: '1px solid #f1f5f9',
                    '&:last-child': { borderBottom: 'none' }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 130 }}>
                      {row.icon}
                      <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                        {row.label}
                      </Typography>
                    </Box>
                    {row.chip ? row.chip : (
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                        {row.value || '—'}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              <Divider sx={{ mx: 3 }} />

              {/* Sección: Fechas */}
              <Box sx={{ px: 3, pt: 2, pb: 2.5 }}>
                <Typography sx={{
                  fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5
                }}>
                  Fechas
                </Typography>

                {/* Fecha de Creación */}
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1, borderBottom: selectedOrder.completedAt ? '1px solid #f1f5f9' : 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 130 }}>
                    <CalendarIcon sx={{ fontSize: 15, color: '#64748b' }} />
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>Creación</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>
                    {formatDate(selectedOrder.createdAt)}
                  </Typography>
                </Box>

                {/* Fecha de Completado (condicional) */}
                {selectedOrder.completedAt && (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', mt: 1.5,
                    py: 1, px: 2, borderRadius: 2,
                    bgcolor: '#f0fdf4', border: '1px solid #bbf7d0'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 130 }}>
                      <CheckCircleIcon sx={{ fontSize: 15, color: '#16a34a' }} />
                      <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>Completado</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#15803d' }}>
                      {formatDate(selectedOrder.completedAt)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mx: 3 }} />

              {/* Sección: Programación por Ente */}
              <Box sx={{ px: 3, pt: 2, pb: 2.5 }}>
                <Typography sx={{
                  fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5
                }}>
                  Programación por Ente
                </Typography>

                {loadingEntityAssignments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : entityAssignments.length === 0 ? (
                  <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>
                    No hay entes registrados para esta orden.
                  </Typography>
                ) : (
                  entityAssignments.map((detail, i) => {
                    const hasSchedule = !!detail.scheduledDate;
                    const timeRange = detail.scheduledTimeStart
                      ? `${detail.scheduledTimeStart}${detail.scheduledTimeEnd ? ` - ${detail.scheduledTimeEnd}` : ''}`
                      : null;
                    const isMine = detail.assignedUserId === currentUser.id;
                    return (
                      <Box key={detail.id || i} sx={{
                        py: 1, px: isMine ? 1 : 0, borderRadius: isMine ? 2 : 0,
                        bgcolor: isMine ? '#e3f2fd' : 'transparent',
                        border: isMine ? '1px solid #90caf9' : 'none',
                        mb: isMine ? 0.5 : 0,
                        borderBottom: !isMine && i < entityAssignments.length - 1 ? '1px solid #f1f5f9' : (isMine ? '1px solid #90caf9' : 'none')
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 15, color: '#64748b' }} />
                            <Typography sx={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>
                              {detail.entityName || (detail.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal')}
                            </Typography>
                            {isMine && (
                              <Chip label="Tú" size="small" sx={{
                                bgcolor: '#1976d2', color: 'white', fontWeight: 700,
                                fontSize: '0.65rem', height: 18
                              }} />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <PersonIcon sx={{ fontSize: 15, color: detail.assignedInspector ? '#1976d2' : '#cbd5e1' }} />
                            <Typography sx={{
                              fontSize: '0.85rem', fontWeight: 600,
                              color: detail.assignedInspector ? '#1e293b' : '#94a3b8'
                            }}>
                              {detail.assignedInspector || 'Sin asignar'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 0.5, pl: 2.7 }}>
                          <AssignmentIcon sx={{ fontSize: 13, color: (detail.serviceNames || []).length ? '#1976d2' : '#cbd5e1' }} />
                          <Typography sx={{ fontSize: '0.76rem', color: (detail.serviceNames || []).length ? '#475569' : '#94a3b8' }}>
                            {(detail.serviceNames || []).length ? detail.serviceNames.join(', ') : 'Sin servicio asignado'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, pl: 2.7 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <EventAvailableIcon sx={{ fontSize: 13, color: hasSchedule ? '#1976d2' : '#cbd5e1' }} />
                            <Typography sx={{ fontSize: '0.76rem', color: hasSchedule ? '#475569' : '#94a3b8' }}>
                              {hasSchedule ? formatDate(detail.scheduledDate) : 'Sin programar'}
                            </Typography>
                          </Box>
                          {timeRange && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                              <AccessTimeIcon sx={{ fontSize: 13, color: '#1976d2' }} />
                              <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                                {timeRange}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3, py: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: 'white',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button
            onClick={handleCloseDetails}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 2 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdenesTrabajoList;
